import React, { createContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true' || false
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: {
            main: '#ff1744',
          },
          background: {
            default: darkMode ? '#121212' : '#f4f6f8',
            paper: darkMode ? '#1e1e1e' : '#fff',
          },
          text: {
            primary: darkMode ? '#f3f4f6' : '#000000',
            secondary: darkMode ? '#d1d5db' : '#333333',
          },
          divider: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
          action: {
            hover: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            selected: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
            disabled: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
            disabledBackground: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)',
          },
        },
        components: {
          // Radio buttons & checkboxes — visible outlines in dark mode
          MuiRadio: {
            styleOverrides: {
              root: darkMode ? { color: 'rgba(255,255,255,0.55)' } : {},
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: darkMode ? { color: 'rgba(255,255,255,0.55)' } : {},
            },
          },
          // Text field outlines
          MuiOutlinedInput: {
            styleOverrides: {
              notchedOutline: darkMode
                ? { borderColor: 'rgba(255,255,255,0.2)' }
                : {},
            },
          },
          // Input labels
          MuiInputLabel: {
            styleOverrides: {
              root: darkMode ? { color: '#d1d5db' } : {},
            },
          },
          // Form labels (used by RadioGroup, FormControl)
          MuiFormLabel: {
            styleOverrides: {
              root: darkMode ? { color: '#d1d5db' } : {},
            },
          },
          // Chips
          MuiChip: {
            styleOverrides: {
              outlined: darkMode
                ? { borderColor: 'rgba(255,255,255,0.2)', color: '#f3f4f6' }
                : {},
            },
          },
          // Dividers
          MuiDivider: {
            styleOverrides: {
              root: darkMode
                ? { borderColor: 'rgba(255,255,255,0.1)' }
                : {},
            },
          },
          // Paper — subtle elevation in dark mode
          MuiPaper: {
            styleOverrides: {
              root: darkMode
                ? { backgroundImage: 'none' }
                : {},
            },
          },
          // Card outlines
          MuiCard: {
            styleOverrides: {
              root: darkMode
                ? { borderColor: 'rgba(255,255,255,0.1)' }
                : {},
            },
          },
        },
      }),
    [darkMode]
  );

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem('darkMode', !darkMode);
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;